// Microsoft Graph access for Generate (spec §3.3, D13): the signed-in user's own delegated
// permissions, from the browser. Silent tokens only during Generate — an interactive prompt is
// opened solely by the Connect Microsoft 365 button (interactive auth from a data path wedges MSAL).
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { msalInstance } from "../auth/msalConfig.js";

export const GRAPH_SCOPES = { files: ["Files.Read.All"], mail: ["Mail.Read"], send: ["Mail.Send.Shared"] };
const GRAPH = "https://graph.microsoft.com/v1.0";
const accountOf = (msal) => msal.getActiveAccount() || msal.getAllAccounts()[0] || null;

export async function getGraphToken(kind, { msal = msalInstance } = {}) {
  try {
    const account = accountOf(msal);
    if (!account) return { ok: false, reason: "signed_out" };
    const r = await msal.acquireTokenSilent({ scopes: GRAPH_SCOPES[kind], account });
    return { ok: true, token: r.accessToken };
  } catch (e) {
    return { ok: false, reason: e instanceof InteractionRequiredAuthError ? "consent" : "error" };
  }
}

export async function connectMicrosoft365({ msal = msalInstance } = {}) {
  const account = accountOf(msal);
  try {
    await msal.acquireTokenPopup({ scopes: [...GRAPH_SCOPES.files, ...GRAPH_SCOPES.mail, ...GRAPH_SCOPES.send], account });
    return { ok: true };
  } catch {
    return { ok: false };
  }
}

const defaultSleep = (ms) => new Promise((r) => setTimeout(r, ms));

export function createGraphGet(token, { fetchImpl = (...a) => fetch(...a), maxPages = 10, sleep = defaultSleep, timeoutMs = 20000 } = {}) {
  // Each individual fetch (the first try and the one retry) gets its own timeout window — the
  // retry sleep doesn't eat into it. A timeout throws a plain Error with status 0, which the
  // collectors already map to their own "didn't respond" notice (same as any other Graph failure).
  const fetchOnce = async (url, init) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      return await fetchImpl(url, { ...init, signal: controller.signal });
    } catch (e) {
      if (controller.signal.aborted) throw Object.assign(new Error("Graph request timed out"), { status: 0 });
      throw e;
    } finally {
      clearTimeout(timer);
    }
  };
  const once = async (url) => {
    const init = { headers: { Authorization: `Bearer ${token}` } };
    let res = await fetchOnce(url, init);
    if (res.status === 429 || res.status === 503) {
      const secs = Math.min(Number(res.headers.get("Retry-After")) || 2, 5);
      await sleep(secs * 1000);
      res = await fetchOnce(url, init);
    }
    if (!res.ok) throw Object.assign(new Error(`Graph request failed (${res.status})`), { status: res.status });
    return res.json();
  };
  return async function graphGet(path, { all = false, maxPages: pageCap = maxPages } = {}) {
    let url = path.startsWith("https://") ? path : GRAPH + path;
    if (!all) return once(url);
    const value = [];
    for (let page = 0; page < pageCap; page++) {
      const body = await once(url);
      value.push(...(body.value || []));
      if (!body["@odata.nextLink"]) return { value, truncated: false };
      url = body["@odata.nextLink"];
    }
    return { value, truncated: true };
  };
}
